import React, { useEffect, useRef, useState } from 'react';

// Antd
import Highlighter from 'react-highlight-words';
import {
    SearchOutlined,
    DeleteOutlined,
    LoadingOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import { Button, Input, Modal, Popconfirm, Space, Spin, Table, Tag, message } from 'antd';

import { Link } from 'react-router-dom';
import { deletePayment, verifyPaymentSlip } from '../../../../Constants/Api/functions/function';
import { generateInvoice } from '../../../../Utils/InvoiceGenerator';

const PaymentsTable = ({ isLoading, paymentData, cources, users, setPayments, role, isSlipTab, onRefresh }) => {
    const [searchText, setSearchText] = useState('');
    const [tableData, setTableData] = useState([]);
    const [searchedColumn, setSearchedColumn] = useState('');
    const [leoadingDeleteId, setLoadingDeleteId] = useState(null);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [rejectPaymentId, setRejectPaymentId] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const searchInput = useRef(null);

    const handleVerifySlip = async (paymentId, status, reason = null) => {
        setActionLoadingId(paymentId);
        try {
            const result = await verifyPaymentSlip(paymentId, status, reason);
            if (result.success) {
                message.success(`Payment slip ${status === 'completed' ? 'verified successfully' : 'rejected successfully'}.`);
                if (onRefresh) {
                    onRefresh();
                }
            } else {
                message.error(result.message || 'Failed to update slip status');
            }
        } catch (error) {
            console.error('handleVerifySlip error:', error);
            message.error('An error occurred while updating status');
        } finally {
            setActionLoadingId(null);
        }
    };

    const openRejectModal = (paymentId) => {
        setRejectPaymentId(paymentId);
        setRejectionReason('');
        setRejectModalVisible(true);
    };

    const handleConfirmReject = async () => {
        if (!rejectPaymentId) return;
        await handleVerifySlip(rejectPaymentId, 'rejected', rejectionReason.trim() || 'Payment slip rejected by admin');
        setRejectModalVisible(false);
        setRejectPaymentId(null);
        setRejectionReason('');
    };

    useEffect(() => {
        let data = paymentData.map((payment) => {
            const user = users?.find((user) => user.id === payment.userId);
            const course = cources?.find((course) => course.id === payment.courseId);
            const breakdown = (course?.installmentBreakdown && course.installmentBreakdown.length > 0)
                ? course.installmentBreakdown
                : (course?.alumniInstallmentBreakdown || []);
            const date = new Date(payment.paymentDate || payment.createdAt);
            return {
                key: payment.id,
                name: user?.name,
                userId: payment?.userId,
                paymentId: payment.id,
                course: course?.name,
                coursePrice: course?.price,
                coursePromoPrice: course?.promoPrice,
                alumniDiscount: course?.alumniDiscount,
                installments: breakdown,
                totalInstallmentsCount: course?.installments || breakdown.length || 0,
                paymentStatus: payment.paymentStatus,
                amount: payment.amount,
                payedInstallments: payment.payedInstallments || 0,
                date: date,
                method: payment.paymentMethod,
                remark: payment.remark,
                slipUrl: payment.slipUrl,
                txnId: payment.paycorpTxnRef || payment.directPayTransactionId
            }
        });

        // Sort data by date
        data.sort((a, b) => b.date - a.date);

        // Convert date back to string format
        data = data.map(item => ({
            ...item,
            date: item.date.toLocaleDateString()
        }));

        setTableData(data);
    }, [paymentData, cources, users]);

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div
                style={{
                    padding: 8,
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{
                        marginBottom: 8,
                        display: 'block',
                    }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({
                                closeDropdown: false,
                            });
                            setSearchText(selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1677ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const handleDeletePayment = async (record) => {
        setLoadingDeleteId(record.paymentId);
        try {
            await deletePayment(record.paymentId);
            const newPayments = paymentData.filter((payment) => payment.id !== record.paymentId);
            setPayments(newPayments);
        } catch (error) {
            console.error('handleDeletePayment', error);
        }
        setLoadingDeleteId(null);
    };

    const getNameSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div
                style={{
                    padding: 8,
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{
                        marginBottom: 8,
                        display: 'block',
                    }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({
                                closeDropdown: false,
                            });
                            setSearchText(selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1677ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text, record) => {
            const isClickable = role !== 'instructor';
            if (searchedColumn === dataIndex) {
                return isClickable ? (
                    <Link to={`/dashboard/users/${record.userId}/overview`}>
                        <Highlighter
                            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                            searchWords={[searchText]}
                            autoEscape
                            textToHighlight={text ? text.toString() : ''}
                        />
                    </Link>
                ) : (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                );
            } else {
                return isClickable ? (
                    <Link to={`/dashboard/users/${record.userId}/overview`}>
                        <p
                            style={{
                                color: '#1677ff',
                                cursor: 'pointer',
                            }}
                        >
                            {text}
                        </p>
                    </Link>
                ) : (
                    <span style={{ color: 'inherit', cursor: 'default' }}>
                        {text}
                    </span>
                );
            }
        },
    });

    const baseColumns = [
        {
            title: 'Course',
            dataIndex: 'course',
            key: 'course',
            width: '25%',
            ...getColumnSearchProps('course'),
        },
        {
            title: 'Payment Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            width: '15%',
        },
        {
            title: 'Amount (LKR)',
            dataIndex: 'amount',
            key: 'amount',
            width: '15%',
            render: (amount) => `${amount} LKR`
        },
        {
            title: 'Paid Installments',
            dataIndex: 'payedInstallments',
            key: 'payedInstallments',
            width: '15%',
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: '15%',
            sorter: (a, b) => {
                const parseDate = (dateStr) => {
                    const [day, month, year] = dateStr.split('/');
                    return new Date(year, month - 1, day);
                };

                return parseDate(a.date) - parseDate(b.date);
            }
        },
        {
            title: 'Method',
            dataIndex: 'method',
            key: 'method',
            width: '15%',
        },
        {
            title: 'Invoice',
            key: 'invoice',
            width: '10%',
            render: (text, record) => {
                const isFullPayment = record.amount === record.coursePrice || record.amount === record.coursePromoPrice;
                const planName = isFullPayment 
                    ? (record.amount === record.coursePromoPrice ? 'Promotional Full Payment' : 'Standard Full Payment') 
                    : 'Installment Plan';
                
                return (
                    <Button 
                        type="link" 
                        icon={<DownloadOutlined />}
                        onClick={() => {
                            generateInvoice({
                                paymentId: record.paymentId,
                                amount: record.amount,
                                userName: record.name,
                                courseName: record.course,
                                coursePrice: record.coursePrice,
                                coursePromoPrice: record.coursePromoPrice,
                                alumniDiscount: record.alumniDiscount,
                                paymentMethod: record.method,
                                paymentStatus: record.paymentStatus,
                                date: record.date,
                                planName: planName,
                                installmentNumber: !isFullPayment && record.payedInstallments > 0 ? record.payedInstallments : undefined,
                                totalInstallments: record.totalInstallmentsCount,
                                installments: record.installments
                            });
                        }}
                    >
                        Download
                    </Button>
                );
            },
        }
    ];

    const adminOnlyColumns = [
        {
            title: 'User Name',
            dataIndex: 'name',
            key: 'name',
            width: '20%',
            ...getNameSearchProps('name'),
        },
        {
            title: 'Course',
            dataIndex: 'course',
            key: 'course',
            width: '20%',
            ...getColumnSearchProps('course'),
        },
        {
            title: 'Payment Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            width: '12%',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: '10%',
        },
        {
            title: 'Paid Installments',
            dataIndex: 'payedInstallments',
            key: 'payedInstallments',
            width: '12%',
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: '10%',
            sorter: (a, b) => {
                const parseDate = (dateStr) => {
                    const [day, month, year] = dateStr.split('/');
                    return new Date(year, month - 1, day);
                };

                return parseDate(a.date) - parseDate(b.date);
            }
        },
        {
            title: 'Method',
            dataIndex: 'method',
            key: 'method',
            width: '10%',
        },
        {
            title: 'Delete Payment',
            key: 'action',
            render: (text, record) => (
                <Popconfirm
                    title="Are you sure you want to delete this payment?"
                    onConfirm={() => handleDeletePayment(record)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Button type="link" danger>
                        {leoadingDeleteId === record.paymentId ? <LoadingOutlined /> : <DeleteOutlined />}
                        Delete
                    </Button>
                </Popconfirm>
            ),
        }
    ];

    const slipColumns = [
        {
            title: 'User Name',
            dataIndex: 'name',
            key: 'name',
            width: 150,
            ...getNameSearchProps('name'),
        },
        {
            title: 'Course',
            dataIndex: 'course',
            key: 'course',
            width: 180,
            ...getColumnSearchProps('course'),
        },
        {
            title: 'Plan / Installment',
            key: 'installmentInfo',
            width: 160,
            render: (text, record) => {
                const total = record.totalInstallmentsCount || 0;
                const paidInst = Number(record.payedInstallments) || 0;
                if (total > 1 && paidInst > 0 && paidInst < total) {
                    return <Tag color="blue">Installment {paidInst} of {total}</Tag>;
                } else if (total > 0 && paidInst >= total) {
                    return <Tag color="green">Full Payment ({total} Inst.)</Tag>;
                } else {
                    return <Tag color="cyan">Full Payment</Tag>;
                }
            }
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            render: (amount) => `${amount} LKR`
        },
        {
            title: 'Txn Ref',
            dataIndex: 'txnId',
            key: 'txnId',
            width: 130,
            render: (txnId) => txnId || '-'
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 110,
        },
        {
            title: 'Slip Receipt',
            dataIndex: 'slipUrl',
            key: 'slipUrl',
            width: 120,
            render: (slipUrl) => (
                slipUrl ? (
                    <a href={slipUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1677ff', fontWeight: 'bold' }}>
                        View Receipt
                    </a>
                ) : 'No Slip'
            )
        },
        {
            title: 'Remark',
            dataIndex: 'remark',
            key: 'remark',
            width: 140,
            render: (remark) => remark || '-'
        },
        {
            title: 'Verify Slip',
            key: 'action',
            width: 170,
            fixed: 'right',
            render: (text, record) => (
                <Space size="small" wrap={false}>
                    <Popconfirm
                        title="Are you sure you want to verify this slip and grant course access?"
                        onConfirm={() => handleVerifySlip(record.paymentId, 'completed')}
                        okText="Yes, Verify"
                        cancelText="Cancel"
                    >
                        <Button
                            type="primary"
                            size="small"
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                            loading={actionLoadingId === record.paymentId}
                        >
                            Verify
                        </Button>
                    </Popconfirm>
                    <Button
                        type="default"
                        size="small"
                        danger
                        loading={actionLoadingId === record.paymentId}
                        onClick={() => openRejectModal(record.paymentId)}
                    >
                        Reject
                    </Button>
                </Space>
            ),
        }
    ];

    let columns = role === 'student' ? baseColumns : adminOnlyColumns;
    if (isSlipTab) {
        columns = slipColumns;
    } else if (role === 'instructor') {
        columns = adminOnlyColumns.filter(col => col.key !== 'action');
    }

    return (
        <div className='table'>
            <Spin
                spinning={isLoading}
                tip="Loading payments..."
            >
                <Table
                    columns={columns}
                    dataSource={tableData}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                    }}
                />
            </Spin>

            <Modal
                title="Reject Payment Slip"
                open={rejectModalVisible}
                onOk={handleConfirmReject}
                onCancel={() => setRejectModalVisible(false)}
                okText="Confirm Rejection"
                okButtonProps={{ danger: true }}
                confirmLoading={actionLoadingId === rejectPaymentId}
            >
                <p>Please enter a reason for rejecting this payment slip (optional):</p>
                <Input.TextArea
                    rows={3}
                    placeholder="e.g. Payment not received in bank account, amount does not match, or receipt is unreadable"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                />
            </Modal>
        </div>
    )
}

export default PaymentsTable